<script setup>
import { computed, h } from 'vue';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
} from '@tanstack/vue-table';
import { useI18n } from 'vue-i18n';
import { formatTime } from '../../constants';
import { useUISettings } from 'dashboard/composables/useUISettings';

import Spinner from 'shared/components/Spinner.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import Table from 'dashboard/components/table/Table.vue';
import Pagination from 'dashboard/components/table/Pagination.vue';
import AgentCell from '../overview/AgentCell.vue';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();

const PAGE_SIZE_KEY = 'report_agent_productivity_table_page_size';

const getPageSize = () => uiSettings.value[PAGE_SIZE_KEY] || 10;

const handlePageSizeChange = pageSize => {
  updateUISettings({ [PAGE_SIZE_KEY]: pageSize });
};

const tableData = computed(() =>
  props.data.map(agent => ({
    agent: agent.name,
    email: agent.email,
    thumbnail: agent.thumbnail,
    status: agent.status,
    open: agent.open,
    closed: agent.closed,
    tmo: agent.tmo,
    csat: agent.csat,
  }))
);

const numberRender = cellProps => {
  const value = cellProps.getValue();
  return h(
    'span',
    {
      class: value ? 'text-n-slate-12' : 'text-n-slate-11',
    },
    value != null ? value : '---'
  );
};

const tmoRender = cellProps => {
  const value = cellProps.getValue();
  return h(
    'span',
    {
      class: value ? 'text-n-slate-12' : 'text-n-slate-11',
    },
    value ? formatTime(value) : '---'
  );
};

const csatRender = cellProps => {
  const value = cellProps.getValue();
  return h(
    'span',
    {
      class: value != null ? 'text-n-slate-12' : 'text-n-slate-11',
    },
    value != null ? `${value}%` : '---'
  );
};

const columnHelper = createColumnHelper();

const columns = computed(() => [
  columnHelper.accessor('agent', {
    header: t('AGENT_PRODUCTIVITY_REPORTS.TABLE.HEADER.AGENT'),
    cell: cellProps => h(AgentCell, cellProps),
    size: 250,
  }),
  columnHelper.accessor('open', {
    header: t('AGENT_PRODUCTIVITY_REPORTS.TABLE.HEADER.OPEN'),
    cell: numberRender,
    size: 100,
  }),
  columnHelper.accessor('closed', {
    header: t('AGENT_PRODUCTIVITY_REPORTS.TABLE.HEADER.CLOSED'),
    cell: numberRender,
    size: 100,
  }),
  columnHelper.accessor('tmo', {
    header: t('AGENT_PRODUCTIVITY_REPORTS.TABLE.HEADER.TMO'),
    cell: tmoRender,
    size: 100,
  }),
  columnHelper.accessor('csat', {
    header: t('AGENT_PRODUCTIVITY_REPORTS.TABLE.HEADER.CSAT'),
    cell: csatRender,
    size: 100,
  }),
]);

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  get columns() {
    return columns.value;
  },
  enableSorting: true,
  enableSortingRemoval: false,
  getCoreRowModel: getCoreRowModel(),
  getSortedRowModel: getSortedRowModel(),
  getPaginationRowModel: getPaginationRowModel(),
  initialState: {
    sorting: [{ id: 'agent', desc: false }],
    pagination: {
      pageSize: getPageSize(),
    },
  },
});
</script>

<template>
  <div class="flex flex-col flex-1">
    <div
      v-if="isLoading"
      class="flex items-center justify-center p-8 text-base"
    >
      <Spinner />
      <span class="ml-2">
        {{ $t('AGENT_PRODUCTIVITY_REPORTS.LOADING') }}
      </span>
    </div>
    <template v-else-if="data.length">
      <Table :table="table" class="max-h-[calc(100vh-21.875rem)]" />
      <Pagination
        class="mt-2"
        :table="table"
        show-page-size-selector
        :default-page-size="getPageSize()"
        @page-size-change="handlePageSizeChange"
      />
    </template>
    <EmptyState v-else :title="$t('AGENT_PRODUCTIVITY_REPORTS.NO_RECORDS')" />
  </div>
</template>
